import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Cpu, CheckCircle, WifiOff, AlertCircle, Thermometer, MapPin, Package, RotateCcw } from "lucide-react";
import Header from "../components/layout/Header";
import { StatCard, Badge, UptimeBar, SearchInput, Button, Table } from "../components/ui/index";
import { noeudsAPI, cascadeAPI } from "../api/services";
import { formatDate } from "../utils/dateUtils";
import { useSocket } from "../context/SocketContext";
import { useAuth } from "../context/AuthContext";
import ActionMenu from "../components/ui/ActionMenu";
import AddNoeudModal from "../components/modals/AddNoeudModal";
import EditNoeudModal from "../components/modals/EditNoeudModal";
import ConfirmDeleteModal from "../components/modals/ConfirmDeleteModal";

const NodeIcon = ({ type }) => {
  const config = type === "Actionneur"
    ? { icon: Cpu, bg: "bg-purple-100", color: "text-purple-600" }
    : { icon: Thermometer, bg: "bg-blue-100", color: "text-blue-600" };
  return (
    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${config.bg}`}>
      <config.icon size={16} className={config.color} />
    </div>
  );
};

export default function Noeuds() {
  const { t } = useTranslation();
  const { user, canDo, adminMode } = useAuth();
  const isRevendeur = user?.role === 'revendeur';
  const isMembre = user?.role === 'membre';
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [editItem, setEditItem]     = useState(null);
  const [deleteItem, setDeleteItem] = useState(null);
  const [toast, setToast] = useState(null);
  const showToast = (msg, type = "error") => { setToast({ msg, type }); setTimeout(() => setToast(null), 4000); };

  // Data state
  const [noeuds, setNoeuds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, enLigne: 0, horsLigne: 0, enStock: 0, affecte: 0 });

  // Cascade filter state
  const [revendeurs, setRevendeurs] = useState([]);
  const [clients, setClients] = useState([]);
  const [projets, setProjets] = useState([]);

  const [filterRevendeur, setFilterRevendeur] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterProjet, setFilterProjet] = useState("");
  const [filterCategorie, setFilterCategorie] = useState("");
  const [filterStatut, setFilterStatut] = useState("");
  const [cardFilter, setCardFilter] = useState("all");
  const toggleCard = (key) => setCardFilter(prev => prev === key ? "all" : key);

  // Socket temps réel
  const { lastStatusChange, lastSensorData } = useSocket();

  useEffect(() => {
    if (!lastStatusChange) return;
    setNoeuds(prev => prev.map(n =>
      n.sn === lastStatusChange.sn
        ? { ...n, etat: lastStatusChange.etat, derniereMaj: new Date().toISOString() }
        : n
    ));
  }, [lastStatusChange]);

  useEffect(() => {
    if (!lastSensorData) return;
    setNoeuds(prev => prev.map(n =>
      n.sn === lastSensorData.sn
        ? { ...n, etat: 'En ligne', derniereMaj: new Date().toISOString() }
        : n
    ));
  }, [lastSensorData]);

  // Fetch noeuds
  const fetchNoeuds = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (filterRevendeur) params.revendeur = filterRevendeur;
      if (filterClient) params.client = filterClient;
      if (filterProjet) params.projet = filterProjet;
      if (filterCategorie) params.categorie = filterCategorie;
      if (filterStatut) params.statut = filterStatut;

      const { data } = await noeudsAPI.getAll(params);
      const list = data.data || data;
      setNoeuds(Array.isArray(list) ? list : []);

      const total = Array.isArray(list) ? list.length : 0;
      const enLigne = Array.isArray(list) ? list.filter(n => n.etat === "En ligne").length : 0;
      const horsLigne = Array.isArray(list) ? list.filter(n => n.etat === "Hors ligne").length : 0;
      const enStock = Array.isArray(list) ? list.filter(n => n.statut === "En stock").length : 0;
      const affecte = Array.isArray(list) ? list.filter(n => n.statut === "Affecté").length : 0;
      setStats({ total, enLigne, horsLigne, enStock, affecte });
    } catch (err) {
      console.error("Erreur chargement nœuds:", err);
    } finally {
      setLoading(false);
    }
  }, [search, filterRevendeur, filterClient, filterProjet, filterCategorie, filterStatut]);

  useEffect(() => { fetchNoeuds(); }, [fetchNoeuds]);

  // Load cascade revendeurs on mount (admin only)
  useEffect(() => {
    if (!isRevendeur) {
      cascadeAPI.revendeurs().then(res => {
        setRevendeurs(res.data.data || res.data || []);
      }).catch(console.error);
    }
  }, [isRevendeur]);

  // Load clients — for revendeur: on mount. For admin: all clients (filtered by revendeur if selected).
  useEffect(() => {
    cascadeAPI.clients(filterRevendeur || undefined).then(res => {
      setClients(res.data.data || res.data || []);
    }).catch(console.error);
  }, [filterRevendeur]);

  // Load projets when client changes
  useEffect(() => {
    cascadeAPI.projets(filterClient || undefined, filterRevendeur || undefined).then(res => {
      setProjets(res.data.data || res.data || []);
    }).catch(console.error);
  }, [filterClient, filterRevendeur]);

  // Handlers
  const handleAdd = async (formData) => {
    try {
      await noeudsAPI.create(formData);
      setShowModal(false);
      fetchNoeuds();
    } catch (err) {
      const msg = err.response?.data?.error || "Erreur lors de l'ajout du nœud";
      showToast(msg);
    }
  };

  const handleEdit = async (updated) => {
    try {
      await noeudsAPI.update(updated._id, updated);
      setEditItem(null);
      fetchNoeuds();
    } catch (err) {
      const msg = err.response?.data?.error || "Erreur lors de la modification du nœud";
      showToast(msg);
    }
  };

  const handleDelete = async () => {
    try {
      await noeudsAPI.remove(deleteItem._id);
      setDeleteItem(null);
      fetchNoeuds();
    } catch (err) {
      console.error("Erreur suppression nœud:", err);
    }
  };

  const handleRetourStock = async (noeud) => {
    try {
      await noeudsAPI.retourStock(noeud._id);
      fetchNoeuds();
    } catch (err) {
      console.error("Erreur retour stock:", err);
    }
  };

  const statutBadge = (statut) => {
    if (statut === "En stock") return "bg-amber-100 text-amber-700";
    return "bg-blue-100 text-blue-700";
  };

  const filteredNoeuds = cardFilter === "all" ? noeuds
    : cardFilter === "enStock" ? noeuds.filter(n => n.statut === "En stock")
    : cardFilter === "enLigne" ? noeuds.filter(n => n.etat === "En ligne")
    : cardFilter === "horsLigne" ? noeuds.filter(n => n.etat === "Hors ligne")
    : noeuds;

  return (
    <>
      <Header title={t('nodes.title')} subtitle={`${stats.total} ${t('nodes.nodesCount')}`} />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 md:gap-4 mb-6">
        <StatCard icon={Cpu} iconBg="bg-blue-100 text-blue-600" label={t('nodes.totalNodes')} value={stats.total} onClick={() => setCardFilter("all")} active={cardFilter === "all"} />
        <StatCard icon={Package} iconBg="bg-amber-100 text-amber-600" label={t('nodes.inStock')} value={stats.enStock} onClick={() => toggleCard("enStock")} active={cardFilter === "enStock"} />
        <StatCard icon={CheckCircle} iconBg="bg-green-100 text-green-600" label={t('nodes.online')} value={stats.enLigne} onClick={() => toggleCard("enLigne")} active={cardFilter === "enLigne"} />
        <StatCard icon={WifiOff} iconBg="bg-red-100 text-red-500" label={t('nodes.offline')} value={stats.horsLigne} onClick={() => toggleCard("horsLigne")} active={cardFilter === "horsLigne"} />
        {canDo('creerNoeud') && (
          <Button onClick={() => setShowModal(true)} className="h-full justify-center">
            <Plus size={18} /> {t('nodes.addToStock')}
          </Button>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 flex-wrap">
        {/* Filtre statut */}
        <select
          value={filterStatut}
          onChange={(e) => setFilterStatut(e.target.value)}
          className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{t('nodes.allStatuses')}</option>
          <option value="En stock">{t('nodes.inStock')}</option>
          <option value="Affecté">{t('nodes.assigned')}</option>
        </select>

        {!isMembre && (
          <>
            {adminMode === 'platform' && (
              <select
                value={filterRevendeur}
                onChange={(e) => { setFilterRevendeur(e.target.value); setFilterClient(""); setFilterProjet(""); }}
                className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">{t('common.allResellers')}</option>
                {revendeurs.map((r) => <option key={r._id} value={r._id}>{r.nom}</option>)}
              </select>
            )}
            <select
              value={filterClient}
              onChange={(e) => { setFilterClient(e.target.value); setFilterProjet(""); }}
              className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('common.allClients')}</option>
              {clients.map((c) => <option key={c._id} value={c._id}>{c.nom}</option>)}
            </select>
            <select
              value={filterProjet}
              onChange={(e) => setFilterProjet(e.target.value)}
              className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">{t('common.allProjects')}</option>
              {projets.map((p) => <option key={p._id} value={p._id}>{p.nom}</option>)}
            </select>
          </>
        )}
        <select
          value={filterCategorie}
          onChange={(e) => setFilterCategorie(e.target.value)}
          className="px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">{t('nodes.allCategories')}</option>
          <option value="Capteur">{t('nodes.sensor')}</option>
          <option value="Actionneur">{t('nodes.actuator')}</option>
          <option value="Passerelle">{t('nodes.gateway')}</option>
        </select>
      </div>

      <div className="mb-4">
        <SearchInput value={search} onChange={setSearch} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-sm text-slate-500">{t('nodes.loading')}</span>
        </div>
      ) : (
        <Table headers={[t('nodes.colNode'), t('nodes.colTypeCategory'), t('nodes.colStatus'), t('nodes.colAssignment'), t('nodes.colState'), t('nodes.colUptime'), t('nodes.colLastUpdate'), t('nodes.colActions')]}>
          {filteredNoeuds.map((n) => (
            <tr key={n._id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <NodeIcon type={n.categorie} />
                  <div>
                    <p className="font-semibold text-slate-800">{n.sn}</p>
                    <p className="text-xs text-slate-400">ID: {n._id?.slice(-6)}</p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <p className="text-sm font-medium text-slate-700">{n.type}</p>
                <p className="text-xs text-slate-400">{n.categorie}</p>
              </td>
              <td className="px-6 py-4">
                <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-semibold ${statutBadge(n.statut)}`}>
                  {n.statut === "En stock" && <Package size={11} className="mr-1" />}
                  {n.statut || "En stock"}
                </span>
              </td>
              <td className="px-6 py-4">
                {n.statut === "Affecté" ? (
                  <div>
                    <p className="text-sm font-medium text-slate-700">{typeof n.projet === 'object' ? n.projet?.nom : '—'}</p>
                    <p className="text-xs text-slate-400">{typeof n.client === 'object' ? n.client?.nom : '—'}</p>
                    {typeof n.chantier === 'object' && n.chantier?.nom && (
                      <p className="text-xs text-slate-500 mt-0.5">📍 {n.chantier.nom}</p>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-slate-400">— {t('nodes.unassigned')}</span>
                )}
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-1.5">
                  <div className={`w-2 h-2 rounded-full ${n.etat === "En ligne" ? "bg-green-500" : "bg-red-500"}`} />
                  <Badge label={n.etat} />
                </div>
              </td>
              <td className="px-6 py-4">
                {n.etat === "En ligne" ? <UptimeBar value={n.uptime} /> : <span className="text-xs text-slate-400">—</span>}
              </td>
              <td className="px-6 py-4 text-sm text-slate-500">
                {formatDate(n.derniereMaj)}
              </td>
              <td className="px-6 py-4">
                <div className="flex items-center gap-1">
                  {n.statut === "Affecté" && (
                    <button
                      onClick={() => handleRetourStock(n)}
                      title={t('nodes.returnToStock')}
                      className="p-1.5 rounded-lg hover:bg-amber-50 text-amber-600 transition-colors"
                    >
                      <RotateCcw size={14} />
                    </button>
                  )}
                  <ActionMenu
                    rowId={n._id}
                    openMenu={openMenu}
                    setOpenMenu={setOpenMenu}
                    onEdit={() => setEditItem(n)}
                    onDelete={() => setDeleteItem(n)}
                  />
                </div>
              </td>
            </tr>
          ))}
        </Table>
      )}

      <EditNoeudModal
        isOpen={!!editItem}
        onClose={() => setEditItem(null)}
        onSave={handleEdit}
        noeud={editItem}
      />

      <ConfirmDeleteModal
        isOpen={!!deleteItem}
        onClose={() => setDeleteItem(null)}
        onConfirm={handleDelete}
        itemName={deleteItem?.sn}
        itemType="nœud"
      />

      <AddNoeudModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onAdd={handleAdd}
      />

      {toast && (
        <div className={`fixed top-6 right-6 z-[60] flex items-center gap-3 px-5 py-3 rounded-xl shadow-lg text-sm font-medium ${
          toast.type === "error" ? "bg-red-600 text-white" : "bg-green-600 text-white"
        }`}>
          <AlertCircle size={16} />
          {toast.msg}
        </div>
      )}
    </>
  );
}
